import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { rhuService } from '../../../../services/rhuService'

const RHUSettings = ({ user, onCodeUpdate }) => {
  const [currentCode, setCurrentCode] = useState('')
  const [newCode, setNewCode] = useState('')
  const [confirmCode, setConfirmCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isCodeUser = user?.accessCode !== undefined
  const isLoginUser = !isCodeUser
  
  // Get the RHU access URL - use production URL
  const rhuAccessURL = 'https://mtoconcepcion.online/#/portal/rural-health'

  const handleDownloadQR = () => {
    const svg = document.getElementById('rhu-qr-code')
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      
      const pngFile = canvas.toDataURL('image/png')
      const downloadLink = document.createElement('a')
      downloadLink.download = 'RHU-Access-QRCode.png'
      downloadLink.href = pngFile
      downloadLink.click()
    }
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const handleCopyURL = () => {
    navigator.clipboard.writeText(rhuAccessURL)
    setSuccess('Access URL copied to clipboard!')
    setTimeout(() => setSuccess(''), 2000)
  }

  const handleUpdateCode = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validation
    if (!currentCode.trim() || !newCode.trim() || !confirmCode.trim()) {
      setError('Please fill in all fields')
      return
    }

    if (newCode !== confirmCode) {
      setError('New code and confirmation do not match')
      return
    }

    if (newCode.length < 6) {
      setError('New code must be at least 6 characters long')
      return
    }

    if (currentCode === newCode) {
      setError('New code must be different from current code')
      return
    }

    setLoading(true)

    try {
      // Verify current code
      const verifyResult = await rhuService.verifyAccessCode(currentCode)
      
      if (!verifyResult.success) {
        setError('Current access code is incorrect')
        setLoading(false)
        return
      }

      // Update to new code
      const updateResult = await rhuService.updateAccessCode(currentCode, newCode)

      if (updateResult.success) {
        setSuccess('Access code updated successfully! Please use your new code next time.')
        
        // Update session storage with new code
        sessionStorage.setItem('rhu_access', JSON.stringify({
          code: newCode,
          accessType: 'code',
          timestamp: new Date().toISOString()
        }))

        // Clear form
        setCurrentCode('')
        setNewCode('')
        setConfirmCode('')

        // Notify parent if callback provided
        if (onCodeUpdate) {
          onCodeUpdate(newCode)
        }
      } else {
        setError(updateResult.error || 'Failed to update access code')
      }
    } catch (err) {
      setError('An error occurred while updating the code')
    }

    setLoading(false)
  }

  return (
    <>
      {/* CSS Animations and Print Styles */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @media print {
          /* Set page size to letter */
          @page {
            size: letter portrait;
            margin: 1in;
          }
          
          /* Hide everything except QR code section */
          .dashboard-header,
          .dashboard-main > *:not(.content-container),
          .notification,
          .box:not(.print-qr-section),
          .section > .container > *:not(.print-qr-section) {
            display: none !important;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            background: white !important;
          }
          
          .section {
            padding: 0 !important;
            height: 100%;
          }
          
          .container {
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .print-qr-section {
            page-break-inside: avoid;
            margin: 0 !important;
            padding: 2rem !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            max-width: 6.5in;
            margin: 0 auto !important;
            height: auto;
          }
          
          .print-hide {
            display: none !important;
          }
          
          .screen-hide {
            display: block !important;
          }
          
          .print-qr-content {
            text-align: center;
            page-break-inside: avoid;
          }
          
          .print-qr-title {
            font-size: 1.8rem !important;
            font-weight: bold;
            margin-bottom: 1.5rem !important;
            color: #000 !important;
            text-align: center;
          }
          
          .print-qr-title i {
            display: none;
          }
          
          /* QR Code styling for print */
          .print-qr-code-container {
            padding: 2rem !important;
            background: white !important;
            border: 3px solid #000 !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            display: inline-block;
          }
          
          #rhu-qr-code {
            width: 250px !important;
            height: 250px !important;
          }
          
          /* URL display for print */
          .print-url-display {
            display: block !important;
            margin-top: 1.5rem !important;
            padding: 1rem 1.5rem !important;
            background: #f3f4f6 !important;
            border: 2px solid #d1d5db !important;
            border-radius: 8px !important;
            text-align: center !important;
            page-break-inside: avoid;
          }
          
          .print-url-display p {
            margin: 0 !important;
            font-size: 0.9rem !important;
            color: #000 !important;
            font-weight: 600 !important;
            word-break: break-all !important;
          }
          
          .print-url-label {
            display: block !important;
            font-size: 0.85rem !important;
            color: #4b5563 !important;
            margin-bottom: 0.5rem !important;
            font-weight: 500 !important;
          }
          
          /* Instructions styling for print */
          .print-instructions {
            margin-top: 2rem !important;
            padding: 1.5rem !important;
            background: #f9fafb !important;
            border: 2px solid #e5e7eb !important;
            border-radius: 8px !important;
            page-break-inside: avoid;
          }
          
          .print-instructions h4 {
            font-size: 1.1rem !important;
            font-weight: 600 !important;
            margin-bottom: 1rem !important;
            color: #000 !important;
          }
          
          .print-instructions h4 i {
            display: none;
          }
          
          .print-instructions ol {
            margin-left: 1.5rem !important;
            font-size: 1rem !important;
            line-height: 2 !important;
            color: #000 !important;
          }
          
          .print-instructions ol li {
            margin-bottom: 0.5rem;
          }
        }
      `}</style>

      <div className="section" style={{ padding: '1.5rem 1rem' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          {/* Header */}
          <div style={{ 
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 10px 30px rgba(139, 92, 246, 0.2)'
          }}>
            <h1 className="title is-3" style={{ 
              color: 'white', 
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fas fa-cog" style={{ marginRight: '0.75rem' }}></i>
              RHU Settings
            </h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: 0 }}>
              Manage your access code and preferences
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="notification is-success is-light" style={{
              borderRadius: '12px',
              border: '2px solid #48c774',
              animation: 'slideInDown 0.3s ease',
              marginBottom: '1.5rem'
            }}>
              <button className="delete" onClick={() => setSuccess('')}></button>
              <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }}></i>
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="notification is-danger is-light" style={{
              borderRadius: '12px',
              border: '2px solid #f14668',
              animation: 'slideInDown 0.3s ease',
              marginBottom: '1.5rem'
            }}>
              <button className="delete" onClick={() => setError('')}></button>
              <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
              {error}
            </div>
          )}

          {/* Update Access Code Section - Only for Code Users */}
          {isCodeUser && (
            <div className="box" style={{
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              animation: 'fadeInUp 0.3s ease'
            }}>
              <h2 className="title is-5" style={{ marginBottom: '1.5rem', color: '#374151' }}>
                <i className="fas fa-key" style={{ marginRight: '0.5rem', color: '#8b5cf6' }}></i>
                Update Access Code
              </h2>

            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ color: '#d97706', fontSize: '1.25rem', marginTop: '0.125rem' }}></i>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#92400e', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                  Important Security Notes:
                </p>
                <ul style={{ marginLeft: '1rem', color: '#78350f', fontSize: '0.85rem', lineHeight: '1.6' }}>
                  <li>Choose a strong, unique code (minimum 6 characters)</li>
                  <li>Mix letters and numbers for better security</li>
                  <li>Do not share your code with anyone</li>
                  <li>You'll need to re-enter this code when accessing the system</li>
                </ul>
              </div>
            </div>

            <form onSubmit={handleUpdateCode}>
              <div className="field">
                <label className="label" style={{ fontWeight: '600', color: '#374151' }}>
                  Current Access Code *
                </label>
                <div className="control has-icons-left">
                  <input
                    className="input"
                    type="password"
                    placeholder="Enter your current code"
                    value={currentCode}
                    onChange={(e) => setCurrentCode(e.target.value.toUpperCase())}
                    style={{ borderRadius: '10px', border: '2px solid #e5e7eb' }}
                  />
                  <span className="icon is-left">
                    <i className="fas fa-lock"></i>
                  </span>
                </div>
              </div>

              <div className="field">
                <label className="label" style={{ fontWeight: '600', color: '#374151' }}>
                  New Access Code *
                </label>
                <div className="control has-icons-left">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter new code (min. 6 characters)"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    minLength={6}
                    style={{ borderRadius: '10px', border: '2px solid #e5e7eb' }}
                  />
                  <span className="icon is-left">
                    <i className="fas fa-key"></i>
                  </span>
                </div>
                <p className="help">
                  Code will be automatically converted to uppercase
                </p>
              </div>

              <div className="field">
                <label className="label" style={{ fontWeight: '600', color: '#374151' }}>
                  Confirm New Code *
                </label>
                <div className="control has-icons-left">
                  <input
                    className="input"
                    type="text"
                    placeholder="Re-enter new code"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.toUpperCase())}
                    style={{ borderRadius: '10px', border: '2px solid #e5e7eb' }}
                  />
                  <span className="icon is-left">
                    <i className="fas fa-check-circle"></i>
                  </span>
                </div>
              </div>

              <div className="field" style={{ marginTop: '2rem' }}>
                <div className="control">
                  <button
                    type="submit"
                    className="button is-primary is-large"
                    disabled={loading}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="icon">
                          <i className="fas fa-spinner fa-spin"></i>
                        </span>
                        <span>Updating Code...</span>
                      </>
                    ) : (
                      <>
                        <span className="icon">
                          <i className="fas fa-save"></i>
                        </span>
                        <span>Update Access Code</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
          )}

          {/* Current Session Info - Only for Code Users */}
          {isCodeUser && (
            <div className="box" style={{
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginTop: '1.5rem',
            background: '#f9fafb'
          }}>
            <h3 className="title is-6" style={{ marginBottom: '1rem', color: '#374151' }}>
              <i className="fas fa-info-circle" style={{ marginRight: '0.5rem', color: '#6b7280' }}></i>
              Session Information
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Access Type:</strong> RHU Staff (Code-Based)
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Current Code:</strong> {user?.accessCode ? '••••••••' : 'Hidden'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Session:</strong> Active (clears on browser close)
              </p>
            </div>
          </div>
          )}

          {/* QR Code Section - Available for All Users */}
          <div className="box print-qr-section" style={{
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginTop: '1.5rem',
            background: 'white'
          }}>
            <h2 className="title is-5 print-qr-title" style={{ marginBottom: '1.5rem', color: '#374151' }}>
              <i className="fas fa-qrcode" style={{ marginRight: '0.5rem', color: '#8b5cf6' }}></i>
              RHU System Access QR Code
            </h2>

            <div className="print-hide" style={{
              background: '#faf5ff',
              border: '1px solid #d8b4fe',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem'
            }}>
              <i className="fas fa-info-circle" style={{ color: '#8b5cf6', fontSize: '1.25rem', marginTop: '0.125rem' }}></i>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#6b21a8', fontSize: '0.875rem', marginBottom: 0 }}>
                  Share this QR code to allow staff members to quickly access the RHU System. 
                  They will still need to enter their access code after scanning.
                </p>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: '1.5rem'
            }} className="print-qr-content">
              {/* QR Code Display */}
              <div className="print-qr-code-container" style={{
                padding: '2rem',
                background: 'white',
                borderRadius: '12px',
                border: '3px solid #8b5cf6',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)'
              }}>
                <QRCodeSVG 
                  id="rhu-qr-code"
                  value={rhuAccessURL}
                  size={200}
                  level="H"
                  includeMargin={true}
                  fgColor="#1f2937"
                  bgColor="#ffffff"
                />
              </div>

              {/* URL Display */}
              <div className="print-hide" style={{ width: '100%' }}>
                <label className="label" style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  Access URL:
                </label>
                <div className="field has-addons">
                  <div className="control is-expanded">
                    <input 
                      className="input" 
                      type="text" 
                      value={rhuAccessURL} 
                      readOnly
                      style={{ 
                        borderRadius: '10px 0 0 10px', 
                        border: '2px solid #e5e7eb',
                        background: '#f9fafb',
                        fontSize: '0.85rem',
                        color: '#374151'
                      }}
                    />
                  </div>
                  <div className="control">
                    <button 
                      className="button is-info"
                      onClick={handleCopyURL}
                      style={{ 
                        borderRadius: '0 10px 10px 0',
                        border: '2px solid #3b82f6',
                        borderLeft: 'none'
                      }}
                      title="Copy URL"
                    >
                      <span className="icon">
                        <i className="fas fa-copy"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="print-hide" style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1rem',
                width: '100%'
              }}>
                <button
                  className="button is-primary"
                  onClick={handleDownloadQR}
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}
                >
                  <span className="icon">
                    <i className="fas fa-download"></i>
                  </span>
                  <span>Download QR</span>
                </button>

                <button
                  className="button is-light"
                  onClick={() => window.print()}
                  style={{
                    borderRadius: '10px',
                    border: '2px solid #e5e7eb',
                    fontWeight: '600'
                  }}
                >
                  <span className="icon">
                    <i className="fas fa-print"></i>
                  </span>
                  <span>Print QR</span>
                </button>
              </div>
            </div>

            {/* URL for Manual Entry - Print Only */}
            <div className="screen-hide print-url-display" style={{ display: 'none' }}>
              <span className="print-url-label">Or enter this URL manually:</span>
              <p>{rhuAccessURL}</p>
            </div>

            {/* Usage Instructions */}
            <div className="print-instructions" style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '10px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                color: '#374151',
                marginBottom: '0.75rem'
              }}>
                <i className="fas fa-lightbulb" style={{ marginRight: '0.5rem', color: '#fbbf24' }}></i>
                How to use:
              </h4>
              <ol style={{ 
                marginLeft: '1.5rem', 
                color: '#6b7280', 
                fontSize: '0.85rem',
                lineHeight: '1.8'
              }}>
                <li>Scan the QR code with a smartphone camera</li>
                <li>Browser will open the RHU System access page</li>
                <li>Enter your access code to login</li>
                <li>Start using the system immediately</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RHUSettings
